import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Panel, Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Globe } from "lucide-react";

const inp = "bg-[#0B111B] border-slate-700 font-mono text-xs h-8";

function ProbeResult({ r, i }) {
  return (
    <div data-testid={`proxy-test-result-${i}`} className="grid grid-cols-[160px_70px_1fr] gap-2 py-1 border-b border-slate-800/60 text-xs items-center">
      <span className="text-slate-300 truncate">{r.name}</span>
      <Pill tone={r.ok ? "green" : "red"}>{r.ok ? "works" : r.status || "error"}</Pill>
      <span className="font-mono text-slate-500 truncate" title={r.final_url}>{r.error || (r.seg_status
        ? `playlist + video segment OK · ${(r.seg_bytes / 1e6).toFixed(1)} MB in ${(r.seg_ms / 1000).toFixed(1)} s (${r.realtime}x real-time)`
        : `${r.kind} · ${r.ms} ms · ${r.final_url}`)}</span>
    </div>
  );
}

export function PullProxies({ s, setS }) {
  const list = s.pull_proxies || [];
  const [url, setUrl] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (i, p) => setS({ ...s, pull_proxies: list.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const test = async () => {
    setBusy(true); setRes(null);
    try { const { data } = await api.post("/admin/proxies/test", { url }); setRes(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Pull proxies (geoblock bypass)" testid="pull-proxies-panel"
      right={<Button data-testid="add-proxy-btn" size="sm" variant="ghost" disabled={list.length >= 20} onClick={() => setS({ ...s, pull_proxies: [...list, { name: "", url: "" }] })}><Plus size={14} /> Proxy</Button>}>
      <p className="text-xs text-slate-400 mb-2">Pull a source through another IP when the provider blocks this server. Use <span className="font-mono">http://user:pass@host:port</span> or <span className="font-mono">socks5://user:pass@host:port</span>, then pick the proxy on the source in the channel editor. Click Save settings to keep changes.</p>
      {list.length === 0 && <p className="text-xs text-slate-500">No proxies configured.</p>}
      {list.map((p, i) => (
        <div key={p.id || `new-${i}`} data-testid={`proxy-row-${i}`} className="grid grid-cols-[180px_1fr_auto] gap-2 mb-2">
          <Input data-testid={`proxy-name-input-${i}`} className={inp} placeholder="name (e.g. Ghana VPS)" value={p.name} onChange={(e) => set(i, { name: e.target.value })} />
          <Input data-testid={`proxy-url-input-${i}`} className={inp} placeholder="socks5://user:pass@1.2.3.4:1080" value={p.url} onChange={(e) => set(i, { url: e.target.value })} />
          <button type="button" data-testid={`proxy-remove-${i}`} onClick={() => setS({ ...s, pull_proxies: list.filter((_, j) => j !== i) })} className="p-1 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="mt-4 pt-3 border-t border-slate-800">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Geoblock test</div>
        <div className="flex gap-2">
          <Input data-testid="proxy-test-url-input" className={inp} placeholder="source URL to test from this server and every saved proxy" value={url} onChange={(e) => setUrl(e.target.value)} />
          <Button data-testid="proxy-test-btn" size="sm" disabled={!url.trim() || busy} onClick={test} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950"><Globe size={13} /> {busy ? "Testing…" : "Test"}</Button>
        </div>
        {res && (
          <div className="mt-2" data-testid="proxy-test-results">
            <div data-testid="proxy-test-verdict" className="text-xs mb-1 text-slate-200">Verdict: <span className="font-semibold">{res.verdict}</span></div>
            {res.results.map((r, i) => <ProbeResult key={i} r={r} i={i} />)}
          </div>
        )}
      </div>
    </Panel>
  );
}
